import { BEIGE_100, BEIGE_1000, BEIGE_600, BLUE_100, BLUE_1000, BLUE_600, CORAL_100, CORAL_1000, CORAL_600, GRAY_100, GRAY_1000, GRAY_600, GRAY_800, GREEN_100, GREEN_1000, GREEN_600, YELLOW_100, YELLOW_1000, YELLOW_600 } from 'constants/styling/theme'
import { BadgeColor, MUIBadge } from '../MUIBadge'
import { Box, Stack } from '@mui/material'
import { FC, Fragment } from 'react'

import { LinearProgressBar } from 'components/common/LinearProgressBar'

type SegmentColor = 'gray' | 'darkGray' | 'beige' | 'blue' | 'coral' | 'green' | 'orange'

interface Keystone {
  label: string
  color?: BadgeColor
  activeColor?: BadgeColor
}

interface Segment {
  end: number
  keystone?: Keystone
  color: SegmentColor
}

interface Props {
  progress: number
  segments?: Segment[]
}

const COLOR_MAP = {
  gray: {
    main: GRAY_600,
    light: GRAY_100,
    dark: GRAY_1000,
  },
  darkGray: {
    main: GRAY_800,
    light: GRAY_100,
    dark: GRAY_1000,
  },
  beige: {
    main: BEIGE_600,
    light: BEIGE_100,
    dark: BEIGE_1000,
  },
  blue: {
    main: BLUE_600,
    light: BLUE_100,
    dark: BLUE_1000,
  },
  coral: {
    main: CORAL_600,
    light: CORAL_100,
    dark: CORAL_1000,
  },
  green: {
    main: GREEN_600,
    light: GREEN_100,
    dark: GREEN_1000,
  },
  orange: {
    main: YELLOW_600,
    light: YELLOW_100,
    dark: YELLOW_1000,
  },
}

/**
 * Component that renders progress bar which can be broken into multiple segments with optional keystones in between segments
 * 
 * @example
 * <SegmentedProgressBar
 *   progress={75}
 *   segments={[
 *     {
 *       end: 50,
 *       color: 'green',
 *       keystone: {
 *         label: 'Half-way',
 *       },
 *     },
 *     {
 *       end: 100,
 *       color: 'coral',
 *     }
 *   ]}
 * />
*/
export const SegmentedProgressBar: FC<Props> = ({
  progress,
  segments = [
    {
      end: 100,
      color: 'gray',
    }
  ]
}) => {
  return (
    <Stack direction="row" flexWrap="nowrap" width="100%" sx={{ boxSizing: 'border-box' }} gap={.5}>

      {segments.sort((a, b) => a.end - b.end).map(({ end, color, keystone }, i) => {

        const prevSegment = segments[i - 1] ?? null

        const range = end - (prevSegment?.end ?? 0)
        const reminder = Math.min(progress - (prevSegment?.end ?? 0), range)

        const localProgress = Math.max(reminder / range) * 100

        return (
          <Fragment key={end}>
            <Stack flex={`1 0 calc(${range}% - .5rem)`} alignItems="center" direction="row" gap={.5}>

              <Box flexGrow="1">
                <LinearProgressBar value={localProgress} barBackgroundColor={COLOR_MAP[color].light} barColor={COLOR_MAP[color].main} />
              </Box>

              {keystone && <MUIBadge label={keystone.label} size="sm" color={localProgress === 100 ? keystone.activeColor ?? 'gray' : keystone.color ?? 'gray'} />}
            </Stack>
          </Fragment>
        )
      })}

    </Stack>
  )
}
